"use client";

import { useEffect } from "react";
import { useTranslations } from "next-intl";
import Link from "@/components/ui/LocaleLink";
import { useConsent } from "@/components/analytics/PostHogProvider";
import { useLocaleSuggestionActive } from "@/lib/locale-suggestion-store";
import { acquireAppFrame, releaseAppFrame, useAppFrameMounted } from "@/lib/app-frame-store";

/**
 * Cookie-consent banner. Renders only when analytics is configured AND the
 * visitor hasn't decided yet (or reopened it via the footer). Accept →
 * PostHog opt-in; Decline → stays opted out. See PostHogProvider for the gate.
 *
 * Yields to `LocaleSuggestion`: if a visitor's browser language doesn't match
 * the page, they're offered their language FIRST and this banner waits, so the
 * consent notice is read in a language they understand (a validity requirement
 * under GDPR / PDPD, not just polish). One prompt on screen at a time; the
 * suggestion resolves on their first interaction, so the wait is bounded.
 *
 * v1 shows for all visitors (conservative default per tickets.md). Geo-narrowing
 * to VN/EEA/UK + rest-of-world exemption is a follow-up via the edge in proxy.ts.
 */
interface CookieConsentProps {
  /** `fixed` (default) floats under the Nav; `inline` renders as an ordinary
   *  flex child, for an app frame that has no document scroll. */
  variant?: "fixed" | "inline";
}

export default function CookieConsent({ variant = "fixed" }: CookieConsentProps) {
  const t = useTranslations("consent");
  const { showBanner, grant, deny } = useConsent();
  const localeSuggestionActive = useLocaleSuggestionActive();

  // The inline copy DECLARES the app frame; the fixed one in the root layout
  // reads that and stands down. Mount/unmount is the signal, so this stays
  // correct across client-side navigation — which a route read in a mount
  // effect would not, since the layout is never remounted. See app-frame-store.
  const appFrameMounted = useAppFrameMounted();
  const inline = variant === "inline";
  useEffect(() => {
    if (!inline) return;
    acquireAppFrame();
    return releaseAppFrame;
  }, [inline]);

  if (appFrameMounted !== inline) return null;  // the other instance owns this route
  if (!showBanner || localeSuggestionActive) return null;

  return (
    <div
      role="region"
      aria-label={t("ariaLabel")}
      // TOP, not bottom (owner, 2026-09-05) — the bottom edge is being given
      // to the mini chat bar. `top: var(--sticky-top)` is the Nav's MEASURED
      // height, published by NavScrollWrapper and set to 0px while the Nav is
      // hidden, so this sits directly under the header and rides up with it on
      // scroll rather than covering it. The border flips to the edge that now
      // faces the page. ⚠️ The 0px fallback is NOT a safe default on an app
      // frame — that variable is stale there, left behind by the previous page
      // — which is why those routes get the inline variant instead of a
      // fallback. See lib/app-frame-store.
      // Frosted at the Nav's own 90% (owner, 2026-10-10): at 97% almost nothing
      // showed through, so it read as a solid strip under a frosted header.
      style={variant === "fixed" ? { top: "var(--sticky-top, 0px)", transition: "top .3s ease" } : undefined}
      className={`${variant === "fixed" ? "fixed inset-x-0 z-sticky" : "shrink-0"} border-b border-navy/15 bg-surface/90 px-21 py-13 backdrop-blur-md sm:px-34`}
    >
      <div className="mx-auto flex max-w-[1200px] flex-col gap-13 sm:flex-row sm:items-center sm:justify-between sm:gap-34">
        <p className="text-xs leading-relaxed text-gray-600 sm:max-w-[68%]">
          {t("message")}{" "}
          <Link
            href="/cookies"
            className="font-semibold text-navy underline underline-offset-2 transition-colors hover:text-navy/70"
          >
            {t("learnMore")}
          </Link>
        </p>
        <div className="flex shrink-0 items-center gap-8">
          <button
            type="button"
            onClick={deny}
            className="rounded-control border border-navy/20 px-21 py-8 text-xs font-semibold uppercase tracking-[0.04em] text-navy transition-colors hover:bg-navy/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
          >
            {t("decline")}
          </button>
          <button
            type="button"
            onClick={grant}
            className="whitespace-nowrap rounded-control bg-gold px-21 py-8 text-xs font-semibold uppercase tracking-[0.04em] text-navy transition-colors hover:bg-gold-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
          >
            {t("accept")}
          </button>
        </div>
      </div>
    </div>
  );
}
